import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Trash2, Mail, Phone } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    setNotes(selected?.notes || "");
  }, [selected]);

  const updateStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    load();
    setSelected((s) => s && s.id === id ? { ...s, status } : s);
  };

  const saveNotes = async () => {
    if (!selected) return;
    await api.patch(`/leads/${selected.id}`, { notes });
    load();
    setSelected({ ...selected, notes });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  return (
    <DashboardLayout role="admin" title="Leads / CRM" subtitle="Every public quote request lands here.">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 card p-0 overflow-hidden">
          <div className="border-b border-white/5 px-5 py-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">All Leads ({leads.length})</h2>
          </div>
          {leads.length === 0 ? (
            <div className="p-12 text-center text-sm" style={{color:'var(--text-muted)'}}>No leads yet. Lead requests from the public site will appear here.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {leads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className={`w-full text-left p-4 px-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors ${selected?.id === l.id ? 'bg-white/[0.03]' : ''}`}
                  data-testid={`lead-row-${l.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold" style={{background:'rgba(212,163,115,0.15)', color:'var(--accent)'}}>
                    {l.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{l.name}</span>
                      <span className={`badge ${l.status === 'new' ? '' : 'badge-muted'} text-[10px] !py-0.5`}>{l.status}</span>
                    </div>
                    <div className="text-xs truncate" style={{color:'var(--text-muted)'}}>
                      {l.service?.replace('_',' ')} · {l.email}
                    </div>
                  </div>
                  <div className="text-xs hidden md:block" style={{color:'var(--text-muted)'}}>
                    {new Date(l.created_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          {selected ? (
            <div className="card p-6 sticky top-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-display text-2xl font-bold">{selected.name}</h3>
                  <div className="text-xs mt-1" style={{color:'var(--text-muted)'}}>
                    {new Date(selected.created_at).toLocaleString()}
                  </div>
                </div>
                <button onClick={() => remove(selected.id)} className="text-red-400 hover:text-red-300" data-testid="lead-delete">
                  <Trash2 size={16}/>
                </button>
              </div>

              <div className="space-y-3 mb-6 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-white" style={{color:'var(--text-muted)'}}>
                  <Mail size={14} style={{color:'var(--accent)'}}/> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 hover:text-white" style={{color:'var(--text-muted)'}}>
                    <Phone size={14} style={{color:'var(--accent)'}}/> {selected.phone}
                  </a>
                )}
                <div className="badge !text-[10px]">{selected.service?.replace('_',' ')}</div>
              </div>

              {selected.message && (
                <div className="mb-6">
                  <div className="label-overline mb-2">Message</div>
                  <p className="text-sm leading-relaxed">{selected.message}</p>
                </div>
              )}

              <div className="mb-5">
                <div className="label-overline mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      data-testid={`status-${s}`}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
                      style={selected.status === s
                        ? { background: 'var(--accent)', color: '#0B132B', borderColor: 'var(--accent)' }
                        : { color: 'var(--text-muted)', borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label-overline mb-2">Internal Notes</div>
                <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes" />
                <button onClick={saveNotes} className="btn-primary mt-3 !py-2 !px-4 !text-sm" data-testid="lead-save-notes">Save notes</button>
              </div>
            </div>
          ) : (
            <div className="card p-10 text-center text-sm" style={{color:'var(--text-muted)'}}>
              Select a lead to view details.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
